<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-card">
        <div class="login-header">
          <h1 class="login-title">{{ tr('login.title') }}</h1>
          <p class="login-subtitle">{{ tr('login.subtitle') }}</p>
        </div>

        <form @submit.prevent="handleLogin" class="login-form">
          <div class="form-group">
            <label for="email" class="form-label">{{ tr('login.emailLabel') }}</label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              class="form-input"
              :placeholder="tr('login.emailPlaceholder')"
              required
              :disabled="loading"
            />
          </div>

          <div class="form-group">
            <label for="password" class="form-label">{{ tr('login.passwordLabel') }}</label>
            <input
              id="password"
              v-model="form.password"
              type="password"
              class="form-input"
              :placeholder="tr('login.passwordPlaceholder')"
              required
              :disabled="loading"
            />
          </div>

          <button
            type="submit"
            class="login-button"
            :disabled="loading || !form.email || !form.password"
          >
            <span v-if="loading" class="loading-spinner">
              <i class="fas fa-spinner fa-spin"></i>
            </span>
            <span v-else>
              <i class="fas fa-sign-in-alt"></i>
              {{ tr('login.submit') }}
            </span>
          </button>
        </form>

        <div class="login-footer">
          <router-link to="/" class="back-link">
            <i class="fas fa-arrow-left"></i>
            {{ tr('login.backHome') }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import { useToast } from '@/composables/useToast';

const router = useRouter();
const route = useRoute();
const { login, loading } = useAuth();
const { error } = useToast();
const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;
const tr = (key: string) => ($t ? $t(key) : key);

const form = ref({
  email: '',
  password: '',
});

watch(
  () => route.query.denied,
  (denied) => {
    if (denied === '1') {
      error(tr('login.deniedMessage'), tr('login.deniedTitle'));
    }
  },
  { immediate: true },
);

const handleLogin = async () => {
  if (!form.value.email || !form.value.password) {
    error(tr('login.requiredMessage'), tr('login.requiredTitle'));
    return;
  }

  const success = await login(form.value.email, form.value.password);

  if (success) {
    // Redirigir al panel de admin o a la ruta original si existe
    const redirectTo = route.query.redirect as string || '/admin';
    router.push(redirectTo);
  }
};

// Limpiar formulario al montar el componente
onMounted(() => {
  form.value = {
    email: '',
    password: '',
  };
});
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at 14% 12%, rgba(219, 174, 80, 0.2), rgba(219, 174, 80, 0) 42%),
    radial-gradient(circle at 86% 8%, rgba(47, 98, 133, 0.2), rgba(47, 98, 133, 0) 38%),
    linear-gradient(132deg, #10293b 0%, #1a3c54 52%, #2f6285 100%);
  padding: var(--spacing-4);
}

.login-container {
  width: 100%;
  max-width: 400px;
}

.login-card {
  background: var(--surface-card-strong);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-2xl);
  border: 1px solid rgba(16, 41, 59, 0.14);
  overflow: hidden;
}

.login-header {
  padding: var(--spacing-8) var(--spacing-6) var(--spacing-6);
  text-align: center;
  background:
    radial-gradient(circle at 80% 30%, rgba(219, 174, 80, 0.16), rgba(219, 174, 80, 0) 50%),
    linear-gradient(135deg, rgba(16, 41, 59, 0.06), rgba(47, 98, 133, 0.08));
}

.login-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary-800);
  margin: 0 0 var(--spacing-2) 0;
  font-family: var(--font-family-display);
}

.login-subtitle {
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
  margin: 0;
}

.login-form {
  padding: 0 var(--spacing-6) var(--spacing-6);
}

.form-group {
  margin-bottom: var(--spacing-4);
}

.form-label {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-2);
}

.form-input {
  width: 100%;
  padding: var(--spacing-3) var(--spacing-4);
  border: 1px solid rgba(16, 41, 59, 0.2);
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  transition: var(--transition-all);
  background: rgba(255, 255, 255, 0.86);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-600);
  box-shadow: 0 0 0 3px rgba(47, 98, 133, 0.14);
}

.form-input:disabled {
  background: var(--color-neutral-100);
  cursor: not-allowed;
}

.login-button {
  width: 100%;
  padding: var(--spacing-3) var(--spacing-4);
  background: linear-gradient(132deg, #173c56 0%, #2f6285 55%, #a77123 100%);
  color: white;
  border: none;
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-all);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
}

.login-button:hover:not(:disabled) {
  filter: brightness(1.05);
  transform: translateY(-1px);
}

.login-button:disabled {
  background: var(--color-neutral-400);
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  display: flex;
  align-items: center;
}

.login-footer {
  padding: var(--spacing-4) var(--spacing-6) var(--spacing-6);
  text-align: center;
}

.back-link {
  color: var(--color-primary-600);
  text-decoration: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  transition: var(--transition-colors);
}

.back-link:hover {
  color: var(--color-primary-700);
}

@media (max-width: 640px) {
  .login-page {
    padding: var(--spacing-2);
  }

  .login-header {
    padding: var(--spacing-6) var(--spacing-4) var(--spacing-4);
  }

  .login-form {
    padding: 0 var(--spacing-4) var(--spacing-4);
  }

  .login-footer {
    padding: var(--spacing-4);
  }
}
</style>
